(() => {
  const theme = window.Maison8 || { routes: {}, strings: {}, cartType: 'page' };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const announce = (message) => {
    const region = document.getElementById('A11yAnnouncer');
    if (!region || !message) return;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  };

  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    if (!opener) return;
    opener.setAttribute('aria-expanded', 'true');
    dialog.addEventListener(
      'close',
      () => {
        opener.setAttribute('aria-expanded', 'false');
        if (document.contains(opener)) opener.focus({ preventScroll: true });
      },
      { once: true }
    );
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog) {
        event.preventDefault();
        openDialog(dialog, opener);
      }
      return;
    }

    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }

    const dialog = event.target;
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      const rect = dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) dialog.close();
    }
  });

  class HeaderBar extends HTMLElement {
    connectedCallback() {
      this.resizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--header-height', `${Math.round(this.offsetHeight)}px`);
      });
      this.resizeObserver.observe(this);

      if (this.hasAttribute('data-overlay')) {
        this.onScroll = () => this.toggleAttribute('data-solid', window.scrollY > 24);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }

  const Cart = {
    sectionId: 'cart-drawer',

    drawer() {
      return document.getElementById('CartDrawer');
    },

    usesDrawer() {
      return theme.cartType === 'drawer' && Boolean(this.drawer());
    },

    async request(url, body) {
      const isForm = body instanceof FormData;
      const response = await fetch(url, {
        method: 'POST',
        headers: isForm
          ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
          : { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: isForm ? body : JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || theme.strings.error);
      }
      return data;
    },

    add(formData) {
      formData.append('sections', this.sectionId);
      formData.append('sections_url', window.location.pathname);
      return this.request(`${theme.routes.cartAdd}.js`, formData);
    },

    change(line, quantity) {
      return this.request(`${theme.routes.cartChange}.js`, {
        line,
        quantity,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });
    },

    render(sections) {
      const html = sections?.[this.sectionId];
      if (!html) return;
      const doc = parseHTML(html);
      const next = doc.getElementById('CartDrawer');
      const drawer = this.drawer();
      if (next && drawer) drawer.innerHTML = next.innerHTML;
      const count = doc.querySelector('[data-cart-count-value]')?.dataset.cartCountValue;
      if (count !== undefined) {
        document.querySelectorAll('[data-cart-count]').forEach((element) => {
          element.textContent = count;
        });
      }
    },
  };

  document.addEventListener('click', async (event) => {
    const control = event.target.closest('[data-cart-change]');
    if (!control) return;
    event.preventDefault();

    const drawer = Cart.drawer();
    const focusKey = control.dataset.focusKey;
    drawer?.setAttribute('aria-busy', 'true');

    try {
      const data = await Cart.change(Number(control.dataset.line), Number(control.dataset.quantity));
      Cart.render(data.sections);
      const target =
        (focusKey && drawer?.querySelector(`[data-focus-key="${focusKey}"]`)) ||
        drawer?.querySelector('[data-close-dialog]');
      target?.focus();
      announce(theme.strings.cartUpdated);
    } catch (error) {
      announce(error.message);
    } finally {
      drawer?.removeAttribute('aria-busy');
    }
  });

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.form?.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
    }

    onSubmit = async (event) => {
      if (!Cart.usesDrawer()) return;
      event.preventDefault();

      const button = this.form.querySelector('button[name="add"]');
      if (!button || button.disabled) return;

      const error = this.querySelector('[data-form-error]');
      if (error) error.hidden = true;
      button.setAttribute('aria-busy', 'true');

      try {
        const data = await Cart.add(new FormData(this.form));
        Cart.render(data.sections);
        openDialog(Cart.drawer(), document.querySelector('[data-cart-trigger]'));
        announce(theme.strings.addedToBag);
      } catch (failure) {
        if (error) {
          error.textContent = failure.message;
          error.hidden = false;
        }
      } finally {
        button.removeAttribute('aria-busy');
      }
    };
  }

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick = (event) => {
      const button = event.target.closest('button[data-step]');
      if (!button || !this.input) return;
      event.preventDefault();
      const min = this.input.min === '' ? 1 : Number(this.input.min);
      const max = this.input.max === '' ? Infinity : Number(this.input.max);
      const current = Number(this.input.value) || min;
      const next = Math.min(max, Math.max(min, current + Number(button.dataset.step)));
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    };
  }

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.controller?.abort();
    }

    onChange = async (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

      const selected = Array.from(this.querySelectorAll('input[type="radio"]:checked'))
        .map((element) => element.dataset.optionValueId)
        .filter(Boolean)
        .join(',');

      const targetUrl = input.dataset.productUrl;
      if (targetUrl && targetUrl !== this.dataset.productUrl) {
        window.location.href = `${targetUrl}?option_values=${selected}`;
        return;
      }

      this.controller?.abort();
      this.controller = new AbortController();
      const sectionId = this.dataset.sectionId;
      const focusedId = document.activeElement?.dataset?.optionValueId;

      try {
        const response = await fetch(
          `${this.dataset.productUrl}?section_id=${sectionId}&option_values=${selected}`,
          { signal: this.controller.signal }
        );
        const doc = parseHTML(await response.text());

        ['Price', 'BuyButtons', 'VariantPicker'].forEach((prefix) => {
          const current = document.getElementById(`${prefix}-${sectionId}`);
          const next = doc.getElementById(`${prefix}-${sectionId}`);
          if (current && next) current.innerHTML = next.innerHTML;
        });

        const info = document.getElementById(`ProductInfo-${sectionId}`);
        const nextInfo = doc.getElementById(`ProductInfo-${sectionId}`);
        const nextVariantId = nextInfo?.querySelector('[data-variant-input]')?.value;
        const variantInput = info?.querySelector('[data-variant-input]');

        if (nextVariantId && variantInput) {
          variantInput.value = nextVariantId;
          const url = new URL(window.location.href);
          url.searchParams.set('variant', nextVariantId);
          url.searchParams.delete('option_values');
          window.history.replaceState({}, '', url);
        }

        const mediaId = nextInfo?.dataset.featuredMediaId;
        if (mediaId) document.getElementById(`Gallery-${sectionId}`)?.showMedia(mediaId);

        if (focusedId) this.querySelector(`[data-option-value-id="${focusedId}"]`)?.focus();
      } catch (error) {
        if (error.name !== 'AbortError') announce(theme.strings.error);
      }
    };
  }

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-gallery-list]');
      this.counter = this.querySelector('[data-gallery-current]');
      if (!this.list || !this.counter) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = Array.prototype.indexOf.call(this.list.children, entry.target);
            this.counter.textContent = String(index + 1);
          });
        },
        { root: this.list, threshold: 0.6 }
      );
      Array.from(this.list.children).forEach((item) => this.observer.observe(item));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    showMedia(mediaId) {
      const item = this.list?.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;
      if (window.matchMedia('(min-width: 990px)').matches) {
        if (item !== this.list.firstElementChild) this.list.prepend(item);
      } else {
        this.list.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
      }
    }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        const doc = parseHTML(await response.text());
        const next = doc.querySelector('product-recommendations');
        if (next && next.innerHTML.trim()) this.innerHTML = next.innerHTML;
      } catch (error) {
        this.hidden = true;
      }
    }
  }

  document.addEventListener('change', (event) => {
    const control = event.target.closest('[data-auto-submit]');
    if (control?.form) control.form.requestSubmit();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-filter-form]')) return;
    event.preventDefault();
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value !== '') params.append(key, value);
    }
    const query = params.toString();
    const base = form.getAttribute('action').split('?')[0];
    window.location.href = query ? `${base}?${query}` : base;
  });

  customElements.define('header-bar', HeaderBar);
  customElements.define('product-form', ProductForm);
  customElements.define('quantity-input', QuantityInput);
  customElements.define('variant-picker', VariantPicker);
  customElements.define('product-gallery', ProductGallery);
  customElements.define('product-recommendations', ProductRecommendations);
})();
